/**
 * Connector (Magento intake) fixtures per features/connectors/SPEC.md:
 * channel_orders across the error states the console needs — unmatched SKU,
 * currency missing, API failure — with raw-payload JSONB samples and revision
 * history, plus one 5-attempt dead-letter, party channel identities, and the
 * nightly reconciliation report rows.
 *
 * Money values inside raw payloads are Magento's own strings — displayed
 * verbatim, never parsed to numbers.
 */
import * as id from "./ids";

export interface Channel {
  id: string;
  code: string;
  name: string;
  name_ar: string;
}

export const channels: Channel[] = [
  { id: id.CHANNEL_MAGENTO, code: "magento-1", name: "Magento Storefront", name_ar: "متجر ماجنتو" },
];

/** Machine-readable failure reasons (proposed wire codes, connectors SPEC OQ1). */
export type ChannelOrderReason = "sku_unmatched" | "currency_missing" | "magento_api_failure";

export interface ChannelOrderRevision {
  rev: number;
  /** Magento-side updated_at that produced this snapshot revision. */
  updated_at: string;
  stored_at: string;
  raw: Record<string, unknown>;
}

export interface ChannelOrder {
  id: string;
  channel_id: string;
  /** Magento entity_id — UNIQUE per channel. */
  external_id: string;
  increment_id: string;
  status: "pending" | "imported" | "error";
  reason: ChannelOrderReason | null;
  error_detail: string | null;
  attempts: number;
  max_attempts: 5;
  /** Latest raw JSONB snapshot (the audit artifact). */
  raw: Record<string, unknown>;
  revisions: ChannelOrderRevision[];
  imported_order_id: string | null;
  dead_lettered_at: string | null;
  ops_notified: "sent" | "pending" | "failed" | null;
  received_at: string;
  updated_at: string;
}

const rawOrder = (
  entityId: string,
  incrementId: string,
  overrides: Record<string, unknown> = {},
): Record<string, unknown> => ({
  entity_id: entityId,
  increment_id: incrementId,
  customer_email: "aisha.alharbi@example.net",
  customer_firstname: "Aisha",
  customer_lastname: "Al-Harbi",
  order_currency_code: "SAR",
  grand_total: "249.0000",
  subtotal: "209.0000",
  shipping_amount: "40.0000",
  items: [
    {
      sku: "WID-CBL-2M",
      name: "USB-C Cable 2m",
      qty_ordered: "2.0000",
      price: "104.5000",
      row_total: "209.0000",
    },
  ],
  ...overrides,
});

export const channelOrders: ChannelOrder[] = [
  {
    // Cleanly imported — backs the imported-orders viewer's raw-snapshot link.
    id: id.CO_IMPORTED,
    channel_id: id.CHANNEL_MAGENTO,
    external_id: "1041",
    increment_id: "100000041",
    status: "imported",
    reason: null,
    error_detail: null,
    attempts: 1,
    max_attempts: 5,
    raw: rawOrder("1041", "100000041"),
    revisions: [
      {
        rev: 1,
        updated_at: "2026-08-07T09:12:00Z",
        stored_at: "2026-08-07T09:12:04Z",
        raw: rawOrder("1041", "100000041"),
      },
    ],
    imported_order_id: id.SO_1001,
    dead_lettered_at: null,
    ops_notified: null,
    received_at: "2026-08-07T09:12:04Z",
    updated_at: "2026-08-07T09:12:31Z",
  },
  {
    // Unmatched SKU — two revisions (Magento order was edited upstream).
    id: id.CO_SKU_UNMATCHED,
    channel_id: id.CHANNEL_MAGENTO,
    external_id: "1042",
    increment_id: "100000042",
    status: "error",
    reason: "sku_unmatched",
    error_detail: 'No variant found for SKU "SKU-XYZ"',
    attempts: 3,
    max_attempts: 5,
    raw: rawOrder("1042", "100000042", {
      grand_total: "199.0000",
      subtotal: "199.0000",
      shipping_amount: "0.0000",
      items: [
        {
          sku: "SKU-XYZ",
          name: "Mystery Widget",
          qty_ordered: "1.0000",
          price: "199.0000",
          row_total: "199.0000",
        },
      ],
    }),
    revisions: [
      {
        rev: 1,
        updated_at: "2026-08-08T14:00:00Z",
        stored_at: "2026-08-08T14:00:05Z",
        raw: rawOrder("1042", "100000042", {
          grand_total: "189.0000",
          subtotal: "189.0000",
          shipping_amount: "0.0000",
          items: [
            {
              sku: "SKU-XYZ",
              name: "Mystery Widget",
              qty_ordered: "1.0000",
              price: "189.0000",
              row_total: "189.0000",
            },
          ],
        }),
      },
      {
        rev: 2,
        updated_at: "2026-08-09T12:40:00Z",
        stored_at: "2026-08-09T12:40:02Z",
        raw: rawOrder("1042", "100000042", {
          grand_total: "199.0000",
          subtotal: "199.0000",
          shipping_amount: "0.0000",
          items: [
            {
              sku: "SKU-XYZ",
              name: "Mystery Widget",
              qty_ordered: "1.0000",
              price: "199.0000",
              row_total: "199.0000",
            },
          ],
        }),
      },
    ],
    imported_order_id: null,
    dead_lettered_at: null,
    ops_notified: null,
    received_at: "2026-08-08T14:00:05Z",
    updated_at: "2026-08-09T12:40:02Z",
  },
  {
    // Currency missing — the raw payload genuinely lacks order_currency_code.
    id: id.CO_CURRENCY_MISSING,
    channel_id: id.CHANNEL_MAGENTO,
    external_id: "1043",
    increment_id: "100000043",
    status: "error",
    reason: "currency_missing",
    error_detail: "Field order_currency_code is absent from the payload",
    attempts: 1,
    max_attempts: 5,
    raw: (() => {
      const raw = rawOrder("1043", "100000043");
      delete raw["order_currency_code"];
      return raw;
    })(),
    revisions: [
      {
        rev: 1,
        updated_at: "2026-08-09T12:41:00Z",
        stored_at: "2026-08-09T12:41:03Z",
        raw: (() => {
          const raw = rawOrder("1043", "100000043");
          delete raw["order_currency_code"];
          return raw;
        })(),
      },
    ],
    imported_order_id: null,
    dead_lettered_at: null,
    ops_notified: null,
    received_at: "2026-08-09T12:41:03Z",
    updated_at: "2026-08-09T12:41:03Z",
  },
  {
    // API failure exhausted its 5 attempts → dead-lettered, ops notified.
    id: id.CO_DEAD_LETTER,
    channel_id: id.CHANNEL_MAGENTO,
    external_id: "1044",
    increment_id: "100000044",
    status: "error",
    reason: "magento_api_failure",
    error_detail: "Magento REST 503 Service Unavailable while fetching order detail",
    attempts: 5,
    max_attempts: 5,
    raw: rawOrder("1044", "100000044", {
      grand_total: "858.0000",
      subtotal: "858.0000",
      shipping_amount: "0.0000",
    }),
    revisions: [
      {
        rev: 1,
        updated_at: "2026-08-09T15:00:00Z",
        stored_at: "2026-08-09T15:00:06Z",
        raw: rawOrder("1044", "100000044", {
          grand_total: "858.0000",
          subtotal: "858.0000",
          shipping_amount: "0.0000",
        }),
      },
    ],
    imported_order_id: null,
    dead_lettered_at: "2026-08-09T18:30:00Z",
    ops_notified: "sent",
    received_at: "2026-08-09T15:00:06Z",
    updated_at: "2026-08-09T18:30:00Z",
  },
  {
    id: id.CO_PENDING,
    channel_id: id.CHANNEL_MAGENTO,
    external_id: "1045",
    increment_id: "100000045",
    status: "pending",
    reason: null,
    error_detail: null,
    attempts: 0,
    max_attempts: 5,
    raw: rawOrder("1045", "100000045"),
    revisions: [
      {
        rev: 1,
        updated_at: "2026-08-10T07:55:00Z",
        stored_at: "2026-08-10T07:55:02Z",
        raw: rawOrder("1045", "100000045"),
      },
    ],
    imported_order_id: null,
    dead_lettered_at: null,
    ops_notified: null,
    received_at: "2026-08-10T07:55:02Z",
    updated_at: "2026-08-10T07:55:02Z",
  },
];

// ── Party channel identities (CODEX-3 match-or-create output) ───────────
export interface PartyChannelIdentity {
  party_id: string;
  channel_id: string;
  external_id: string;
  matched_by: "channel_identity" | "email" | "created";
  created_at: string;
}

export const partyChannelIdentities: PartyChannelIdentity[] = [
  {
    party_id: id.PARTY_AISHA,
    channel_id: id.CHANNEL_MAGENTO,
    external_id: "742",
    matched_by: "email",
    created_at: "2026-06-15T11:30:00Z",
  },
];

// ── Nightly reconciliation report ────────────────────────────────────────
export interface ReconciliationDiscrepancy {
  increment_id: string;
  kind: "missing" | "total_mismatch";
  magento_total: string;
  erp_total: string | null;
}

export interface ReconciliationDay {
  day: string;
  magento_count: number;
  magento_total: string;
  erp_count: number;
  erp_total: string;
  status: "clean" | "discrepant";
  last_run_at: string;
  discrepancies: ReconciliationDiscrepancy[];
}

export const reconciliationDays: ReconciliationDay[] = [
  {
    day: "2026-08-09",
    magento_count: 214,
    magento_total: "51240.5000",
    erp_count: 213,
    erp_total: "51003.2500",
    status: "discrepant",
    last_run_at: "2026-08-10T02:00:00Z",
    discrepancies: [
      { increment_id: "100000046", kind: "missing", magento_total: "237.2500", erp_total: null },
    ],
  },
  {
    day: "2026-08-08",
    magento_count: 198,
    magento_total: "47882.0000",
    erp_count: 198,
    erp_total: "47882.0000",
    status: "clean",
    last_run_at: "2026-08-10T02:00:00Z",
    discrepancies: [],
  },
];
